import { ADULTO_MASCULINO } from '../../dominio/didatico/perfisVitima'
import { POSE_ALINHADA, type PoseVitima } from '../../dominio/didatico/rigVitima'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { VitimaProcedural, type TrajeManequim } from '../vitima/VitimaProcedural'

/**
 * Bombeiros da mesa com o MESMO corpo das vítimas (`VitimaProcedural`),
 * vestidos de EPI: capa e calça de aramida, faixas refletivas, luvas, botas e
 * capacete. Antes eram bonecos de blocos, de outra família visual.
 */

/** Tecido cáqui/ouro da capa de combate a incêndio. */
const CAQUI = '#a16207'
const CAQUI_CALCA = '#8a5306'
const FAIXA = '#fef08a'
const PRATA = '#e5e7eb'
const LUVA = '#ea580c'
const BOTA = '#141619'

const TRAJE_EPI: TrajeManequim = { roupa: CAQUI, calca: CAQUI_CALCA, calcado: BOTA, luva: LUVA, mangaLonga: true }

/** De pé, braços soltos e levemente à frente — prontos para trabalhar. */
const POSE_DE_PE: PoseVitima = {
  ...POSE_ALINHADA,
  quadrilRad: 0,
  joelhoRad: 0.04,
  abducaoCoxaRad: 0.07,
  ombroRad: -0.18,
  cotoveloRad: -0.45,
  cabecaInclinacaoRad: 0.04,
}

const p = ADULTO_MASCULINO
/** Altura da pelve com o corpo de pé (a mesma da vítima "em pé" da mesa). */
const Y_PELVE = 0.92

const semRaycast = () => null

/** Capacete de bombeiro: casco, aba mais longa atrás, emblema e viseira. */
function Capacete({ cor, cruz = false }: { cor: string; cruz?: boolean }) {
  const r = p.raioCabeca
  return (
    <group position={[0, r * 0.18, 0]}>
      <mesh raycast={semRaycast} castShadow scale={[1.18, 1.0, 1.25]}>
        <sphereGeometry args={[r, 20, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={cor} roughness={0.3} metalness={0.1} />
      </mesh>
      {/* crista central do casco */}
      <mesh raycast={semRaycast} position={[0, r * 0.92, -r * 0.1]} scale={[0.25, 0.22, 1.1]}>
        <sphereGeometry args={[r, 12, 8]} />
        <meshStandardMaterial color={cor} roughness={0.3} />
      </mesh>
      {/* aba: curta na frente, longa atrás para proteger a nuca */}
      <mesh raycast={semRaycast} castShadow position={[0, 0, -r * 0.18]} scale={[1.45, 1, 1.75]}>
        <cylinderGeometry args={[r, r * 1.02, r * 0.1, 24]} />
        <meshStandardMaterial color={cor} roughness={0.35} />
      </mesh>
      {/* emblema frontal */}
      <mesh raycast={semRaycast} position={[0, r * 0.48, r * 1.12]} rotation={[-0.4, 0, 0]}>
        <boxGeometry args={[r * 0.6, r * 0.5, 0.008]} />
        <meshStandardMaterial color={cruz ? '#ffffff' : '#e5c46b'} metalness={cruz ? 0 : 0.6} roughness={0.35} />
      </mesh>
      {cruz && (
        <group position={[0, r * 0.48, r * 1.13]} rotation={[-0.4, 0, 0]}>
          <mesh raycast={semRaycast}>
            <boxGeometry args={[r * 0.42, r * 0.12, 0.01]} />
            <meshStandardMaterial color="#ef4444" />
          </mesh>
          <mesh raycast={semRaycast}>
            <boxGeometry args={[r * 0.12, r * 0.42, 0.01]} />
            <meshStandardMaterial color="#ef4444" />
          </mesh>
        </group>
      )}
      {/* viseira recolhida sob a aba */}
      <mesh raycast={semRaycast} position={[0, -r * 0.05, r * 0.95]} rotation={[0.25, 0, 0]}>
        <boxGeometry args={[r * 1.5, r * 0.32, 0.006]} />
        <meshStandardMaterial color="#cbd5e1" transparent opacity={0.45} roughness={0.05} />
      </mesh>
    </group>
  )
}

/** Faixas refletivas em volta do tronco: amarela larga com prata, como na capa real. */
function FaixasDoTronco() {
  const rx = (p.larguraTronco / 2) * 1.04
  const rz = (p.profundidadeTronco / 2) * 1.08
  return (
    <group>
      {[0.28, 0.58].map((f) => (
        <group key={f} position={[0, p.alturaTronco * f, 0]}>
          <mesh raycast={semRaycast} scale={[rx, 1, rz]}>
            <cylinderGeometry args={[1, 1, 0.055, 28, 1, true]} />
            <meshStandardMaterial color={FAIXA} emissive={FAIXA} emissiveIntensity={0.25} roughness={0.4} side={2} />
          </mesh>
          <mesh raycast={semRaycast} scale={[rx * 1.005, 1, rz * 1.005]}>
            <cylinderGeometry args={[1, 1, 0.018, 28, 1, true]} />
            <meshStandardMaterial color={PRATA} metalness={0.7} roughness={0.25} side={2} />
          </mesh>
        </group>
      ))}
      {/* gola levantada da capa */}
      <mesh raycast={semRaycast} position={[0, p.alturaTronco - 0.01, 0]} scale={[rx * 0.62, 1, rz * 0.8]}>
        <cylinderGeometry args={[1, 1.05, 0.07, 20, 1, true]} />
        <meshStandardMaterial color={CAQUI} roughness={0.8} side={2} />
      </mesh>
    </group>
  )
}

function CorpoDeBombeiro({ capacete, acessorioTronco }: { capacete: React.ReactNode; acessorioTronco?: React.ReactNode }) {
  return (
    <VitimaProcedural
      proporcoes={p}
      colocacao={{ posicaoPelve: { x: 0, y: Y_PELVE, z: 0 }, guinadaRad: 0, reclinacaoRad: 0, rolagemRad: 0 }}
      pose={POSE_DE_PE}
      traje={TRAJE_EPI}
      acessorioCabeca={capacete}
      acessorioTronco={
        <>
          <FaixasDoTronco />
          {acessorioTronco}
        </>
      }
    />
  )
}

/** Bombeiro de salvamento: capacete vermelho, capa cáqui, sem EPR nas costas. */
export function Bombeiro3D() {
  return (
    <group name="bombeiro">
      <CorpoDeBombeiro capacete={<Capacete cor="#dc2626" />} />
    </group>
  )
}

/**
 * Bombeiro médico (APH): capacete branco com cruz, bolsa de primeiros socorros
 * vermelha numa mão e cilindro de O2 verde na outra.
 */
export function BombeiroMedico3D() {
  return (
    <group name="bombeiro-medico">
      <CorpoDeBombeiro capacete={<Capacete cor="#f8fafc" cruz />} />
      {/* bolsa vermelha pendendo da mão direita */}
      <group position={[-0.34, 0.5, 0.12]}>
        <mesh raycast={semRaycast} castShadow>
          <CaixaArredondada args={[0.16, 0.26, 0.36]} raio={0.04} />
          <meshStandardMaterial color="#dc2626" roughness={0.75} />
        </mesh>
        <mesh raycast={semRaycast} position={[-0.081, 0, 0]}>
          <boxGeometry args={[0.005, 0.1, 0.03]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
        <mesh raycast={semRaycast} position={[-0.081, 0, 0]}>
          <boxGeometry args={[0.005, 0.03, 0.1]} />
          <meshStandardMaterial color="#ffffff" />
        </mesh>
        <mesh raycast={semRaycast} position={[0, 0.06, 0]}>
          <boxGeometry args={[0.165, 0.025, 0.365]} />
          <meshStandardMaterial color={FAIXA} emissive={FAIXA} emissiveIntensity={0.2} />
        </mesh>
        <mesh raycast={semRaycast} position={[0, 0.18, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.06, 0.01, 6, 14, Math.PI]} />
          <meshStandardMaterial color="#1e293b" />
        </mesh>
      </group>
      {/* cilindro de O2 verde na mão esquerda */}
      <group position={[0.33, 0.52, 0.12]}>
        <mesh raycast={semRaycast} castShadow>
          <cylinderGeometry args={[0.055, 0.055, 0.42, 16]} />
          <meshStandardMaterial color="#15803d" roughness={0.4} metalness={0.3} />
        </mesh>
        <mesh raycast={semRaycast} position={[0, 0.23, 0]}>
          <sphereGeometry args={[0.055, 14, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#f8fafc" roughness={0.4} />
        </mesh>
        <mesh raycast={semRaycast} position={[0, 0.3, 0]}>
          <cylinderGeometry args={[0.02, 0.025, 0.06, 10]} />
          <meshStandardMaterial color="#cbd5e1" metalness={0.85} roughness={0.2} />
        </mesh>
        <mesh raycast={semRaycast} position={[0, 0.3, 0.03]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.025, 0.025, 0.012, 14]} />
          <meshStandardMaterial color="#f8fafc" />
        </mesh>
      </group>
    </group>
  )
}
